/**
 * Cookie Consent Banner Component
 * 
 * GDPR-compliant cookie consent banner for analytics tracking.
 * - Appears on first visit
 * - Remembers user's choice
 * - Provides link to Privacy Policy
 * - Matches premium dark theme
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Cookie, X, Shield, ChevronRight } from 'lucide-react';
import {
  hasConsentDecision,
  grantConsent,
  denyConsent,
} from '../lib/posthog';

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Check if user has already made a consent decision
    const hasDecision = hasConsentDecision();
    
    // Show banner after a short delay if no decision made
    if (!hasDecision) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500); // 1.5 second delay for better UX

      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    grantConsent();
    setIsVisible(false);
  };

  const handleDecline = () => {
    denyConsent();
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ 
            type: 'spring', 
            stiffness: 300, 
            damping: 30,
            delay: 0.1 
          }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 pointer-events-none"
        >
          <div className="max-w-2xl mx-auto pointer-events-auto">
            <div 
              className="relative overflow-hidden rounded-2xl border border-white/10 
                         bg-gradient-to-br from-void-200/95 to-void-300/95 
                         backdrop-blur-xl shadow-2xl"
            >
              {/* Subtle gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-aurora/5 to-slate/5 pointer-events-none" />
              
              <div className="relative p-5">
                {/* Close button */}
                <button
                  onClick={handleDecline}
                  className="absolute top-3 right-3 p-1.5 rounded-lg text-white/40 
                           hover:text-white/70 hover:bg-white/5 transition-all"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Header */}
                <div className="flex items-start gap-4 mb-4">
                  <div 
                    className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ 
                      background: 'linear-gradient(135deg, #dc464a 0%, #f97171 100%)',
                      boxShadow: '0 0 20px rgba(220, 70, 74, 0.3)'
                    }}
                  >
                    <Cookie className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 pr-6">
                    <h3 className="text-white font-semibold text-base mb-1">
                      We value your privacy
                    </h3>
                    <p className="text-white/60 text-sm leading-relaxed">
                      We use cookies to improve your experience and understand how you use our platform.
                      {!isExpanded && (
                        <button 
                          onClick={() => setIsExpanded(true)}
                          className="ml-1.5 font-medium transition-all inline-flex items-center gap-0.5 hover:gap-1"
                          style={{ color: '#f97171' }}
                        >
                          Learn more
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </p>
                  </div>
                </div>

                {/* Expanded details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="bg-void/50 rounded-xl p-4 mb-4 border border-white/5">
                        <div className="flex items-start gap-3 mb-3">
                          <Shield className="w-4 h-4 text-slate-light mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="text-white/90 font-medium text-sm mb-1">
                              Your data is protected
                            </h4>
                            <ul className="text-white/50 text-xs space-y-1">
                              <li>• Session recordings mask all sensitive inputs</li>
                              <li>• We never capture passwords or payment details</li>
                              <li>• No personal data is shared with third parties</li>
                              <li>• You can change your preference anytime in Settings</li>
                            </ul>
                          </div>
                        </div>
                        <Link 
                          to="/privacy" 
                          className="text-aurora hover:text-aurora-light text-xs transition-colors"
                          onClick={() => setIsVisible(false)}
                        >
                          Read our full Privacy Policy →
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Action buttons */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleAccept}
                    className="flex-1 px-5 py-2.5 rounded-xl font-medium text-sm
                             text-white transition-all duration-200
                             hover:scale-[1.02] active:scale-[0.98]"
                    style={{ 
                      background: 'linear-gradient(135deg, #dc464a 0%, #f97171 100%)',
                      boxShadow: '0 0 30px rgba(220, 70, 74, 0.3)'
                    }}
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleDecline}
                    className="flex-1 px-5 py-2.5 rounded-xl font-medium text-sm
                             bg-white/[0.06] border border-white/10
                             text-white/80 hover:text-white hover:bg-white/[0.08]
                             transition-all duration-200"
                  >
                    Decline
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
